import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import PractisingCh from './PractisingCh';
import AssistantsTab from './AssistantsTab';
import SidebarNav from './Sidebar';
import IDBadgeTab from './IDBadgeTab';
import EventsTab from './EventsTab';
import HelpCenterTab from './HelpCenterTab';
import Whistleblow from './Whistleblow';
import LicensingAdimin from './LicensingAdimin';
import NewResource from './NewResource';
import ResourceTab from './ResourceTab';
import UserMgt from './UserMgt';
import ResetPass from './ResetPass';
import ComplaintsTab from './ComplaintsTab';
import ComplimentsTab from './ComplimentsTab';

const DashboardContent = ({
    activeTab,
    loading,
    error,
    userDetails,
}) => {
    const renderContent = () => {
        switch (activeTab) {
            case 'Licenses':
                return <PractisingCh />;
            case 'Licensing Admin':
                return <LicensingAdimin />;
            case 'News':
                return <NewResource />;
            case 'Resources':
                return <ResourceTab />;
            case 'Complaints':
                return <ComplaintsTab />;
            case 'Compliments':
                return <ComplimentsTab />;
            case 'User Management':
                return <UserMgt />;
            case 'ID Badge':
                return <IDBadgeTab />;
            case 'Assistants':
                return <AssistantsTab />;
            case 'Events':
                return <EventsTab />;
            case "Reset Password":
                return <ResetPass />;
            case 'Whistleblow':
                return <Whistleblow />;
            case 'Help Center':
                return <HelpCenterTab />;
            default:
                return <div className=''>Welcome to your Dashboard!</div>;
        }
    };

    return (
        <div className="container mx-auto px-4 lg:px-8 py-8">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 gap-4">
                <h1 className="text-3xl sm:text-4xl font-bold text-gray-800 tracking-tight">{activeTab}</h1>

                {!loading && userDetails && userDetails.user &&
                    <div className="flex items-center gap-3 bg-white px-4 py-2 rounded-full shadow-sm border border-gray-200">
                        <span className='font-medium text-gray-700'>Welcome, {userDetails.user.name}</span>
                        <i className="fa fa-user-check text-green-500"></i>
                    </div>
                }
            </div>

            <motion.section
                key={activeTab}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, ease: "easeInOut" }}
                className="min-h-[60vh] transition-all"
            >
                {loading ? (
                    <div className="flex flex-col items-center justify-center min-h-[300px]">
                        <svg className="animate-spin h-12 w-12 text-sky-600 mb-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        <span className="text-gray-600 text-lg font-medium">Loading Dashboard...</span>
                    </div>
                ) : error ? (
                    <div className="flex flex-col items-center justify-center min-h-[200px] text-red-600 bg-red-50 p-8 rounded-lg">
                        <i className="fa fa-exclamation-triangle text-4xl mb-3" />
                        <span className='text-xl font-bold'>{error}</span>
                        <p className='text-sm mt-1'>There was an error loading your data. Please try again later.</p>
                    </div>
                ) : (
                    renderContent()
                )}
            </motion.section>
        </div>
    );
};

const FinalDashboardL = () => {
    const [activeTab, setActiveTab] = useState(() => {
        const accountType = localStorage.getItem('account_type');
        return accountType === 'staff' ? 'Licensing Admin' : 'Licenses';
    });
    const [isSidebarOpen, setIsSidebarOpen] = useState(window.innerWidth >= 1024);
    const [userDetails, setUserDetails] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const navigate = useNavigate();

    useEffect(() => {
        const handleResize = () => {
            if (window.innerWidth >= 1024) {
                setIsSidebarOpen(true);
            } else {
                setIsSidebarOpen(false);
            }
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        const fetchData = async () => {
            const accessToken = localStorage.getItem('accessToken');
            if (!accessToken) {
                navigate('/portal');
                return;
            }

            try {
                setLoading(true);
                setError(null);

                // Check steps completion
                const stepsResponse = await fetch('https://portal.lsb.go.ke/account/steps/', {
                    method: 'GET',
                    headers: {
                        'Authorization': `Bearer ${accessToken}`,
                        'Content-Type': 'application/json',
                    },
                });

                if (stepsResponse.ok) {
                    const stepsData = await stepsResponse.json();
                    const allStepsCompleted = Object.values(stepsData).every(step => step === true);
                    if (!allStepsCompleted) {
                        navigate('/maindashboard');
                        return;
                    }
                }

                const response = await fetch('https://portal.lsb.go.ke/account/me', {
                    method: 'GET',
                    headers: {
                        'Authorization': `Bearer ${accessToken}`,
                        'Content-Type': 'application/json',
                    },
                });

                if (!response.ok) {
                    throw new Error('Failed to fetch user data');
                }

                const data = await response.json();
                setUserDetails(data);
            } catch (error) {
                setError(error.message);
                // Only navigate to portal on authentication/authorization error, NOT generic fetch failures
                if (
                    error.message.includes('Failed to fetch user data') ||
                    error.message.toLowerCase().includes('unauthorized') ||
                    error.message.toLowerCase().includes('forbidden')
                ) {
                    navigate('/portal');
                }
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [navigate]);

    const handleLogout = () => {
        Swal.fire({
            title: 'Are you sure?',
            text: "You will be logged out of your account.",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Yes, log out!'
        }).then((result) => {
            if (result.isConfirmed) {
                localStorage.removeItem('accessToken');
                navigate('/portal');
                Swal.fire(
                    'Logged Out!',
                    'You have been successfully logged out.',
                    'success'
                )
            }
        });
    };

    return (
        <div className="flex h-screen overflow-hidden bg-gray-50">
            <div className="w-64 flex-shrink-0">
                <SidebarNav
                    userDetails={userDetails}
                    activeTab={activeTab}
                    setActiveTab={setActiveTab}
                    handleLogout={handleLogout}
                    isSidebarOpen={isSidebarOpen}
                    setIsSidebarOpen={setIsSidebarOpen}
                />
            </div>

            <motion.main
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: 0.2 }}
                className="flex-1 h-full overflow-y-auto"
            >
                <DashboardContent
                    activeTab={activeTab}
                    loading={loading}
                    error={error}
                    userDetails={userDetails}
                />
            </motion.main>
        </div>
    );
};

export default FinalDashboardL;